// The Account page and the account key panel of the Devices page (D41, D59).
import type { ReactNode } from "react";

import type { AccountKeyMode } from "../../../account/api";
import type { AccountRole } from "../../../account/setup";
import { plural, rich } from "../../helpers";

const p = plural("en");

export default {
  page: {
    label: "Your account",
    profile: "Profile",
    profileHint: "How you sign in to this host. Only the owner can change an email.",
    added: (date: string) => `Added ${date}`,
    changePassword: "Change password",
    changePasswordHint: "Signing in needs this password; reading conversations also needs a linked device.",
    passwordChanged: "Password changed; other sessions were signed out",
    passwordLastChanged: (date: string) => `Password last changed ${date}`,
    sessions: "Sessions",
    sessionsHint: "Changing your password signs out every other session.",
    thisBrowser: "This browser",
    cookie: "Signed in with an HttpOnly session cookie",
    signOut: "Sign out",
  },
  panel: {
    title: "Account key",
    subtitle: "Signs the list of devices that can read your conversations",
    fingerprint: "Fingerprint",
    lossWarning: (and: ReactNode) =>
      rich("Lose every device ", and, " the recovery file, and past conversations cannot be read again by anyone. Keep the file somewhere offline."),
    lossWarningAnd: "and",
  },
  card: {
    checking: "Checking the account key…",
    unavailable: (message: string) => `Account key status unavailable: ${message}`,
    heldBy: {
      managed: "The platform",
      holder: "This browser",
      mismatch: "Another key",
      create: "Nobody yet",
      restore: "Another device",
      waiting: "Another device",
    } satisfies Record<AccountRole, string>,
    mode: { self: "Self-managed", managed: "Managed by the platform" } satisfies Record<AccountKeyMode, string>,
    facts: { heldBy: "Held by", mode: "Mode", recipients: "Recipients", deviceList: "Device list" },
    recipientCount: (n: number) => p(n, { one: "# device", other: "# devices" }),
    listVersion: (v: number) => `version ${v}`,
    noListYet: "none yet",
    noKeyYet: "No account key yet. The first browser you link chooses who holds it.",
    managedUnlinked: "The platform holds the account key; it adds devices you approve to the list.",
    linkToManage: "Link this browser to approve devices or restore the key from its recovery file.",
    managedSubtitle: "Managed by the platform",
    managedStrong: "The platform holds this account key and can read your conversations.",
    managedNote:
      " When you approve a device, the platform adds it to the device list and gives it your past conversations; there is no recovery file.",
    mismatchStrong: "This browser's account key is not the account's.",
    mismatchNote: " This browser will not sign device lists with a different one.",
    waitingSubtitle: "Waiting to be approved",
    restoreNote: "The device holding the account key approves this browser into the device list.",
    waitingNote: "Another device signs the device list; it approves this browser.",
    holderStrong: "This browser holds the account key.",
    holderNote: (command: ReactNode) =>
      rich(
        " Compare the fingerprint with ",
        command,
        " on the host before pinning. Approve devices signs a new list with every active device, so new conversations are encrypted to them.",
      ),
    listSigned: (version: number) => `Device list version ${version} signed`,
    signing: "Signing…",
    approve: "Approve devices",
  },
  choose: {
    managedLabel: "Let the platform manage the account key",
    managedStrong: "The platform can read your conversations.",
    managedNote:
      "It keeps the account key, signs your device list and gives every device you approve your past conversations, so there is no recovery file to keep. Anyone who controls this host can read what your agents and you write. This cannot be changed later.",
    handingOver: "Handing over…",
    letPlatform: "Let the platform manage it",
    label: "Choose who holds the account key",
    question: "Who should hold the account key?",
    questionNote: "It signs the list of devices your conversations are encrypted to. Choose once, on this first device.",
    myDevices: "My devices",
    myDevicesNote:
      ": only your devices can read conversations. You keep a recovery file; lose every device and the file, and past conversations are gone.",
    platform: "The platform",
    platformNote: ": no recovery file and new devices see old conversations, but the platform can read your conversations.",
    keepOnMyDevices: "Keep it on my devices",
  },
  create: {
    saveLabel: "Save the recovery file",
    saveStrong: "Save the account key's recovery file.",
    saveNote:
      " Download it and check it is saved (on a phone: the Files app, Downloads) somewhere other than this device; with it and the passphrase the account key can be restored if this browser's storage is lost. You can download it again until you finish; after that it is never offered again.",
    downloadAgain: "Download again",
    download: "Download recovery file",
    finishing: "Finishing…",
    finish: "I have saved it, finish",
    title: "Create the account key",
    note: (min: number) =>
      `No device holds the account key yet, so this browser will. It signs the list of devices conversations are encrypted to. Choose a recovery passphrase (at least ${min} characters) for the recovery file.`,
    passphrase: "Recovery passphrase",
    confirm: "Confirm recovery passphrase",
    generating: "Generating…",
    submit: "Create account key",
  },
  restore: {
    label: "Restore the account key",
    lead: "Lost the browser that held the account key? Restore it here from its recovery file.",
    file: "Recovery file",
    passphrase: "Recovery passphrase",
    restoring: "Restoring…",
    submit: "Restore account key",
  },
  errors: {
    listExists: "a recipient list already exists: another device holds the account key",
    notEnrolled: "this browser is not among the enrolled devices",
    sealingMismatch: (id: string) => `the sealing key does not match its id ${id}`,
    differentKey: (fingerprint: string) => `this recovery file is for a different account key (the API has ${fingerprint})`,
    unknownKind: (kind: string) => `unknown device kind ${kind}`,
    noDevices: "no devices to sign a recipient list for",
    passphraseTooShort: (min: number) => `Use at least ${min} characters.`,
    passphrasesDiffer: "The two passphrases differ.",
    notJson: "not a recovery file (not JSON)",
    notRecoveryFile: "not an OpenCloudSecurity account-key recovery file",
    unsupportedKdf: "unsupported key derivation in the recovery file",
    unsupportedCipher: "unsupported cipher in the recovery file",
    noPublicKey: "the recovery file has no public key",
    wrongPassphrase: "wrong passphrase, or the file was changed",
    keyMismatch: "the private key in the file does not match its public key",
  },
};
